import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import {
  Typography,
  Box,
  TextField,
  InputLabel,
  Select,
  Autocomplete,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  Button,
  Snackbar,
  CircularProgress,
  IconButton
} from '@mui/material'
import {
  AddBoxOutlined,
  VisibilityOutlined,
  AccessTimeOutlined,
  Clear
} from '@mui/icons-material'
import { FormProvider, useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Advertiser,
  CampaignInput,
  CampaignResponse
} from '../../types/campaign'
import theme from '../theme'
import OneColumnLayout from '../OneColumnLayout'
import NewCampaign from '../icons/NewCampaign'
import {
  fetchOwners,
  fetchAdvertisers,
  createAdvertiser,
  createCampaign,
  fetchCampaign,
  updateCampaign,
  fetchContentCategories,
  gtvOwnerId,
  hubspotDealRequiredOwnerIds
} from '../getData'
import { UserAuthResponse } from '../../types/campaign'
import { CampaignInputSchema, ErrorResponse } from '../../types/campaign'
import InputErrorMessage from '../InputErrorMessage'
import AppModal from '../AppModal'
import CircularLoader from '../CircularLoader'
import AlertMessage, { AlertContents } from '../AlertMessage'
import ConfirmationModal from '../ConfirmationModal'

interface Props {
  user: UserAuthResponse | null
  defaultValues?: Partial<CampaignInput>
}

const CreateCampaign = ({ user, defaultValues }: Props) => {
  const isGtvUser = user ? user?.company.id === gtvOwnerId : false
  const formMethods = useForm<CampaignInput>({
    mode: 'all',
    resolver: yupResolver(CampaignInputSchema),
    defaultValues: {
      name: '',
      owner_id: user?.company.id,
      advertiser_id: undefined,
      delivery_type: 'impressions',
      content_category_id: undefined,
      hubspot_deal_id: '',
      ...defaultValues
    }
  })
  const navigate = useNavigate()
  const { id: campaignId } = useParams()
  const location = useLocation()

  const {
    control,
    formState: { errors },
    setError,
    handleSubmit,
    getValues,
    setValue,
    watch
  } = formMethods
  const [advertisers, setAdvertisers] = useState<Advertiser[]>([])
  const [newAdvertiser, setNewAdvertiser] = useState<Advertiser | null>(null)
  const [openAdModal, setOpenAdModal] = useState(false)
  const [advertiserInput, setAdvertiserInput] = useState('')
  const [openSnackbar, setOpenSnackbar] = useState({ open: false, message: '' })
  const [alertContents, setAlertContents] = useState<AlertContents | null>(null)
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false)

  const { data: fetchedCampaign } = useQuery({
    queryKey: ['campaign', campaignId],
    queryFn: () => fetchCampaign(Number(campaignId)),
    enabled: Boolean(!location.state?.campaign && campaignId)
  })

  const campaign: CampaignResponse = location.state?.campaign || fetchedCampaign

  useEffect(() => {
    if (campaign) {
      formMethods.reset({
        name: campaign.name ?? '',
        owner_id: campaign.owner?.id ?? undefined,
        advertiser_id: campaign.advertiser?.id ?? undefined,
        delivery_type: campaign.delivery_type ?? 'impressions',
        content_category_id: campaign.content_category?.id ?? undefined,
        hubspot_deal_id: campaign.hubspot_deal_id ?? '',
      })
    }
  }, [campaign, campaignId])

  const { data: owners } = useQuery({
    queryKey: ['owners'],
    queryFn: fetchOwners,
    retry: false
  })

  const { data: advertiserResponse, refetch: refetchAdvertisers } = useQuery({
    queryKey: ['advertisers'],
    queryFn: fetchAdvertisers,
    retry: false
  })

  const { data: contentCategories, error: contentCategoriesError } = useQuery({
    queryKey: ['contentCategories'],
    queryFn: fetchContentCategories,
    retry: false,
    enabled: isGtvUser
  })

  const parentContentCategories = contentCategories?.filter((category) => category.parent_id === null)

  const ownerId = watch('owner_id')

  const handleCloseAdvertiserModal = () => {
    setOpenAdModal(false)
    setAlertContents(null)
    setAdvertiserInput('')
  }

  const { mutate: createAdvertiserMutation, isPending: isCreatingAdvertiser } =
    useMutation({
      mutationFn: createAdvertiser,
      onSuccess: (response) => {
        refetchAdvertisers()

        handleCloseAdvertiserModal()
        setNewAdvertiser(response)

        setOpenSnackbar({
          open: true,
          message: 'Advertiser created successfully'
        })
      },
      onError: (error: AxiosError) => {
        console.error(error)
        const errorData = error.response?.data as ErrorResponse
        setAlertContents({
          open: true,
          message: errorData?.message || 'Error creating advertiser',
          severity: 'error'
        })
      }
    })

  const getUniqueAdvertisers = (advertisers: Advertiser[]) => {
    const dedupedList = Object.fromEntries(
      advertisers.map((advertiser) => [advertiser.name, advertiser])
    )
    return Object.values(dedupedList)
  }

  useEffect(() => {
    if (advertiserResponse && advertiserResponse.length > 0) {
      const list = getUniqueAdvertisers(advertiserResponse)
      setAdvertisers(list)

      if (newAdvertiser) {
        setValue('advertiser_id', newAdvertiser.id)
        setNewAdvertiser(null)
      }
    }
  }, [advertiserResponse, newAdvertiser])

  const { mutate: createCampaignMutation, isPending: isCreatingCampaign } =
    useMutation({
      mutationFn: createCampaign,
      onSuccess: (response) => {
        setOpenSnackbar({
          open: true,
          message: 'Campaign created successfully'
        })
        navigate(`/campaign/${response.id}`)
      },
      onError: (error: AxiosError) => {
        const errorData = error.response?.data as ErrorResponse
        if (
          errorData.message.includes(
            'direct_ad_campaigns_hubspot_deal_id_unique'
          )
        ) {
          setError('hubspot_deal_id', {
            message: 'This ID is already in use'
          })
        } else {
          setOpenSnackbar({
            open: true,
            message: errorData.message
          })
        }
      }
    })

  const { mutate: updateCampaignMutation, isPending: isUpdatingCampaign } =
    useMutation({
      mutationFn: (campaign: CampaignInput) =>
        updateCampaign(campaign, Number(campaignId)),
      onSuccess: () => {
        navigate(`/campaign/${campaignId}`)
      },
      onError: (error: AxiosError) => {
        const errorData = error.response?.data as ErrorResponse
        setOpenSnackbar({
          open: true,
          message: errorData?.message || 'Error updating campaign'
        })
      }
    })

  const handleSaveCampaign = handleSubmit(() => {
    const formData = getValues()

    if (campaignId) {
      updateCampaignMutation(formData)
    } else {
      createCampaignMutation(formData)
    }
  })

  const header = (
    <Typography
      variant="heading-8"
      sx={{
        width: '65%',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: theme.space['24']
      }}
    >
      <NewCampaign
        sx={{
          color: theme.palette['base-black'],
          marginRight: theme.space['28'],
          height: '28px',
          width: '28px'
        }}
      />
      {campaignId ? 'Edit Campaign' : 'New Campaign'}
    </Typography>
  )

  const footerActions = (
    <Box
      sx={{
        marginTop: theme.space['40'],
        display: 'flex',
        gap: theme.space['20']
      }}
    >
      <Button
        variant="contained"
        color="secondary"
        sx={{
          width: '340px',
          height: '56px'
        }}
        size="large"
        onClick={() => {
          if (campaignId) {
            navigate(`/campaign/${campaignId}`)
          } else {
            setOpenConfirmationModal(true)
          }
        }}
      >
        <Typography variant="ui-3">Cancel</Typography>
      </Button>
      <Button
        data-testid="campaign-save-button"
        color="primary"
        variant="contained"
        onClick={handleSaveCampaign}
        sx={{
          width: '340px',
          height: '56px'
        }}
        size="large"
        disabled={isCreatingCampaign || isUpdatingCampaign}
      >
        <Typography variant="ui-3">{campaignId ? 'Save' : 'Create'}</Typography>
      </Button>
    </Box>
  )

  const getInputLabel = (field: string, text: string) => {
    return (
      <InputLabel
        id={`${field}-label`}
        sx={{
          marginBottom: theme.space['8'],
          color:
            field in errors
              ? theme.palette['red-500']
              : theme.palette['sage-900']
        }}
      >
        <Typography variant="body-11" sx={{ fontWeight: 500 }}>
          {text}
        </Typography>
      </InputLabel>
    )
  }

  const advertiserModal = (
    <AppModal
      open={openAdModal}
      onClose={handleCloseAdvertiserModal}
      header={<Typography variant="heading-4">Add Advertiser</Typography>}
      content={
        <Box
          sx={{ marginTop: theme.space['24'], marginBottom: theme.space['24'] }}
        >
          {alertContents?.open && (
            <AlertMessage
              alert={alertContents}
              onClose={() => setAlertContents(null)}
            />
          )}
          {isCreatingAdvertiser ? (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <CircularProgress sx={{ color: theme.palette['accent-500'] }} />
            </Box>
          ) : (
            <Box>
              {getInputLabel('advertiser_name', 'Name')}
              <TextField
                placeholder="Enter advertiser name"
                variant="outlined"
                sx={{ width: '100%' }}
                value={advertiserInput}
                onChange={(e) => setAdvertiserInput(e.target.value)}
              />
            </Box>
          )}
        </Box>
      }
      footer={
        !isCreatingAdvertiser && (
          <Box sx={{ display: 'flex', gap: '12px' }}>
            <Button
              variant="contained"
              color="secondary"
              sx={{ width: '264px', height: '56px' }}
              onClick={handleCloseAdvertiserModal}
            >
              <Typography variant="ui-3">Cancel</Typography>
            </Button>
            <Button
              data-testid="advertiser-save-button"
              variant="contained"
              color="primary"
              disabled={!advertiserInput}
              sx={{ width: '264px', height: '56px' }}
              onClick={() =>
                createAdvertiserMutation({ name: advertiserInput })
              }
            >
              <Typography variant="ui-3">Save</Typography>
            </Button>
          </Box>
        )
      }
    />
  )

  return (
    <Box>
      {advertiserModal}
      {isCreatingCampaign || (isUpdatingCampaign && <CircularLoader />)}
      <ConfirmationModal
        open={openConfirmationModal}
        onClose={() => setOpenConfirmationModal(false)}
        onConfirm={() => navigate('/')}
        title="Cancel Campaign"
        message="Are you sure you want to cancel this campaign? All changes will be lost."
        isLoading={false}
        confirmButtonText="Yes"
        cancelButtonText="No"
      />
      <Snackbar
        open={openSnackbar.open}
        autoHideDuration={5000}
        onClose={() => setOpenSnackbar({ open: false, message: '' })}
        message={openSnackbar.message}
      />
      <OneColumnLayout
        header={header}
        footer={footerActions}
        data-testid="campaign-form"
      >
        <FormProvider {...formMethods}>
          <Controller
            name="name"
            control={control}
            rules={{
              required: 'This field is required'
            }}
            render={({ field }) => (
              <Box>
                {getInputLabel('name', 'Campaign Name')}
                <TextField
                  {...field}
                  value={field.value ?? ''}
                  type="text"
                  variant="outlined"
                  placeholder="Campaign Name"
                  sx={{ width: '100%' }}
                  error={!!errors.name}
                  data-testid="name-input"
                />
                {errors.name && (
                  <InputErrorMessage error={errors.name.message} />
                )}
              </Box>
            )}
          />
          <Controller
            name="owner_id"
            control={control}
            rules={{
              required: 'This field is required'
            }}
            render={({ field }) => (
              <Box sx={{ display: user?.company.id === gtvOwnerId ? 'block' : 'none'}}>
                {getInputLabel('owner_id', 'Owner')}
                <Select
                  {...field}
                  value={field.value ?? ''}
                  variant="outlined"
                  labelId="owner-label"
                  displayEmpty
                  renderValue={(value) => {
                    if (value) {
                      return owners?.find((owner) => owner.id === value)?.name
                    }
                    return (
                      <span style={{ color: theme.palette['sage-200'] }}>
                        Select Owner
                      </span>
                    )
                  }}
                  sx={{ width: '100%' }}
                  error={!!errors.owner_id}
                  data-testid="owner-input"
                  endAdornment={
                    field.value ? (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation()
                          field.onChange(undefined)
                        }}
                        sx={{
                          position: 'absolute',
                          right: 30,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          zIndex: 1
                        }}
                      >
                        <Clear fontSize="small" />
                      </IconButton>
                    ) : null
                  }
                >
                  {owners?.map((owner) => (
                    <MenuItem
                      key={owner.id}
                      value={owner.id}
                      data-testid={`owner-option-${owner.id}`}
                    >
                      {owner.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.owner_id && (
                  <InputErrorMessage error={errors.owner_id.message} />
                )}
              </Box>
            )}
          />
          <Controller
            name="advertiser_id"
            control={control}
            rules={{
              required: 'This field is required'
            }}
            render={({ field }) => (
              <Box>
                {getInputLabel('advertiser_id', 'Advertiser')}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    border: '1px solid',
                    borderRadius: theme.borders['rounded-10'],
                    paddingRight: '4px',
                    borderColor: errors.advertiser_id
                      ? theme.palette['red-500']
                      : theme.palette['sage-200']
                  }}
                >
                  <Autocomplete
                    {...field}
                    value={
                      advertisers?.find(
                        (advertiser) => advertiser.id === field.value
                      ) || null
                    }
                    options={advertisers ?? []}
                    getOptionLabel={(option) => option?.name || ''}
                    filterOptions={(options, { inputValue }) => {
                      const searchTerm = inputValue.toLowerCase()
                      return options.filter((advertiser) =>
                        advertiser.name.toLowerCase().includes(searchTerm)
                      )
                    }}
                    renderOption={(props, option, state) => (
                      <MenuItem
                        key={`advertiser-${option.id}-${state.index}`}
                        {...props}
                      >
                        {option.name}
                      </MenuItem>
                    )}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    onChange={(event, value) => field.onChange(value?.id)}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Select Advertiser" />
                    )}
                    freeSolo={false}
                    autoComplete={false}
                    clearOnBlur
                    clearOnEscape
                    sx={{
                      width: '100%',
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none'
                      }
                    }}
                    data-testid="advertiser-input"
                  />
                  <Box
                    sx={{
                      borderLeft: '1px solid',
                      borderColor: theme.palette['sage-200'],
                      width: theme.space['48'],
                      textAlign: 'center'
                    }}
                  >
                    <Button
                      data-testid="add-advertiser-button"
                      size="small"
                      sx={{
                        height: '28px',
                        minWidth: '28px',
                        padding: 0
                      }}
                      onClick={() => setOpenAdModal(true)}
                    >
                      <AddBoxOutlined
                        sx={{
                          color: theme.palette['sage-900'],
                          fontSize: '24px'
                        }}
                      />
                    </Button>
                  </Box>
                </Box>
                {errors.advertiser_id && (
                  <InputErrorMessage error={errors.advertiser_id.message} />
                )}
              </Box>
            )}
          />
          <Controller
            name="delivery_type"
            control={control}
            rules={{
              required: 'This field is required'
            }}
            render={({ field }) => (
              <Box>
                {getInputLabel('delivery_type', 'Delivery Type')}
                <ToggleButtonGroup
                  {...field}
                  value={field.value}
                  onChange={(event, value) => field.onChange(value)}
                  exclusive
                  disabled={campaign?.flights.length > 0}
                  sx={{
                    width: '100%',
                    '& .MuiButtonBase-root': {
                      border: '1px solid',
                      borderColor: theme.palette['sage-200'],
                      borderRadius: theme.borders['rounded-10'],
                      height: '56px'
                    },
                    '& .Mui-selected.Mui-disabled': {
                      backgroundColor: theme.palette['sage-200'],
                      borderColor: theme.palette['sage-200']
                    }
                  }}
                >
                  <ToggleButton
                    value="impressions"
                    sx={{
                      width: '50%',
                      '&.Mui-selected': {
                        backgroundColor: theme.palette['sage-900'],
                        color: theme.palette['base-white']
                      }
                    }}
                  >
                    <VisibilityOutlined
                      fontSize="inherit"
                      sx={{ marginRight: theme.space['8'] }}
                    />
                    <Typography variant="body-8">Impressions</Typography>
                  </ToggleButton>
                  <ToggleButton
                    value="play_frequency"
                    sx={{
                      width: '50%',
                      '&.Mui-selected': {
                        backgroundColor: theme.palette['sage-900'],
                        color: theme.palette['base-white']
                      }
                    }}
                  >
                    <AccessTimeOutlined
                      fontSize="inherit"
                      sx={{ marginRight: theme.space['8'] }}
                    />
                    <Typography variant="body-8">Play Frequency</Typography>
                  </ToggleButton>
                </ToggleButtonGroup>
                {errors.delivery_type && (
                  <InputErrorMessage error={errors.delivery_type.message} />
                )}
              </Box>
            )}
          />
            <Controller
              name="hubspot_deal_id"
              control={control}
              render={({ field }) => (
                <Box sx={{ display: ownerId !== null && hubspotDealRequiredOwnerIds.includes(ownerId) ? 'block' : 'none' }}>
                  {getInputLabel('hubspot_deal_id', 'Hubspot Deal ID')}
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    variant="outlined"
                    type="text"
                    placeholder="Type your Hubspot ID"
                    sx={{
                      width: '100%'
                    }}
                    error={!!errors.hubspot_deal_id}
                    data-testid="hubspot-input"
                  />
                  {errors.hubspot_deal_id && (
                    <InputErrorMessage error={errors.hubspot_deal_id.message} />
                  )}
                </Box>
              )}
            />
            {isGtvUser && (
              <Controller
                name="content_category_id"
                control={control}
                rules={{
                  required: 'Please select a content category for this campaign'
                }}
                render={({ field }) => (
                  <Box>
                    {getInputLabel('content_category_id', 'Content Category')}
                    {contentCategoriesError ? (
                      <Box
                        sx={{
                          padding: theme.space['16'],
                          border: `1px solid ${theme.palette['red-500']}`,
                          borderRadius: theme.borders['rounded-10'],
                          backgroundColor: theme.palette['red-100']
                        }}
                      >
                        <Typography variant="body-11" color="error">
                          Categories unavailable
                        </Typography>
                      </Box>
                    ) : (
                      <Autocomplete
                        {...field}
                        value={
                          parentContentCategories?.find(
                            (category) => category.id === field.value
                          ) || null
                        }
                        options={parentContentCategories ?? []}
                        getOptionLabel={(option) => option?.name || ''}
                        filterOptions={(options, { inputValue }) => {
                          const searchTerm = inputValue.toLowerCase()
                          return options.filter((category) =>
                            category.name.toLowerCase().includes(searchTerm)
                          )
                        }}
                        renderOption={(props, option) => {
                          const liProps = props as React.HTMLAttributes<HTMLLIElement> & { key?: string }
                          const optionKey = liProps.key
                          const restProps = { ...liProps } as React.HTMLAttributes<HTMLLIElement> & { key?: string }
                          delete restProps.key
                          return (
                            <MenuItem key={optionKey} {...(restProps as React.HTMLAttributes<HTMLLIElement>)}>
                              {option.name}
                            </MenuItem>
                          )
                        }}
                        isOptionEqualToValue={(option, value) =>
                          option.id === value.id
                        }
                        onChange={(event, value) => field.onChange(value?.id)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select Content Category"
                            error={!!errors.content_category_id}
                          />
                        )}
                        freeSolo={false}
                        autoComplete={false}
                        clearOnBlur
                        clearOnEscape
                        sx={{
                          width: '100%',
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: errors.content_category_id
                              ? `1px solid ${theme.palette['red-100']}`
                              : `1px solid ${theme.palette['sage-200']}`
                          }
                        }}
                        data-testid="content-category-input"
                      />
                    )}
                    {errors.content_category_id && (
                      <InputErrorMessage error={errors.content_category_id.message} />
                    )}
                  </Box>
                )}
              />
            )}
        </FormProvider>
      </OneColumnLayout>
    </Box>
  )
}

export default CreateCampaign
